/* Month calendar of processed attendance for one employee. */
import type { DailyAttendance, DayStatus } from "../../../data/types";
import { useI18n } from "../../../i18n";
import { monthEnd, weekday } from "../../../domain/dates";
import { WEEKDAYS_SHORT, dayStatusLabel } from "./labels";

export const DAY_CLASS: Record<DayStatus, string> = {
  Present: "present", Late: "late", Absent: "absent", "On leave": "leave", Weekend: "weekend", "Missing punch": "missing", "Not in yet": "notin",
};

export function monthStats(rows: DailyAttendance[]) {
  const c = (s: DayStatus) => rows.filter((r) => r.status === s).length;
  return { present: c("Present"), late: c("Late"), absent: c("Absent"), leave: c("On leave"), missing: c("Missing punch"), otHours: Math.round(rows.reduce((a, r) => a + r.overtimeMinutes, 0) / 6) / 10 };
}

export function MonthGrid({ period, rows, today }: { period: string; rows: DailyAttendance[]; today: string }) {
  const { L, isAr } = useI18n();
  const first = period + "-01";
  const last = Number(monthEnd(first).slice(8, 10));
  const lead = weekday(first);
  const byDate = new Map(rows.map((r) => [r.date, r]));
  const cells: React.ReactNode[] = [];
  for (let k = 0; k < lead; k++) cells.push(<div key={"e" + k} />);
  for (let d = 1; d <= last; d++) {
    const ds = period + "-" + String(d).padStart(2, "0");
    const r = byDate.get(ds);
    const cls = r ? DAY_CLASS[r.status] : "future";
    const label = r ? dayStatusLabel(r.status, L) : "";
    const times = r && r.firstIn ? r.firstIn + (r.lastOut ? "–" + r.lastOut : "") : "";
    cells.push(
      <div key={ds} className={"day " + cls + (ds === today ? " today" : "")} data-testid={"day-" + ds} data-status={r?.status ?? "future"} title={r ? label + (times ? " · " + times : "") : undefined}>
        <b>{d}</b><span>{label}</span>{times && <small dir="ltr">{times}</small>}
      </div>,
    );
  }
  return (
    <>
      <div className="month" data-testid="month-grid">
        {WEEKDAYS_SHORT.map((w, k) => <div key={k} className="mh">{isAr ? w[1] : w[0]}</div>)}
        {cells}
      </div>
      <div className="legend mt">
        {([["present", "Present", "success"], ["late", "Late", "warning"], ["absent", "Absent", "danger"], ["missing", "Missing punch", "warning"], ["leave", "On leave", "info"], ["weekend", "Weekend", "border-strong"]] as [string, DayStatus, string][]).map(([k, s, c]) => (
          <span key={k}><i style={{ background: `var(--${c})` }} />{dayStatusLabel(s, L)}</span>
        ))}
      </div>
    </>
  );
}
